/*
 * Copyright (c) RECREWT GmbH 2023.
 */

export function getColorFromImage(image: HTMLImageElement, x: number, y: number) {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;

  const context = canvas.getContext('2d')!;
  context.drawImage(image, 0, 0);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

  const index = (y * imageData.width + x) * 4;
  const r = imageData.data[index];
  const g = imageData.data[index + 1];
  const b = imageData.data[index + 2];
  const a = imageData.data[index + 3] / 255;

  return { r, g, b, a };
}

export function rgbaToHex(r: number, g: number, b: number, a: number) {
  const outParts = [
    r.toString(16),
    g.toString(16),
    b.toString(16),
    Math.round(a * 255)
      .toString(16)
      .substring(0, 2),
  ];

  // Pad single-digit output values
  outParts.forEach(function (part, i) {
    if (part.length === 1) {
      outParts[i] = '0' + part;
    }
  });

  return '#' + outParts.join('');
}

/** `#RRGGBB` or `#RGB`. Fair colours come from operator-entered config, so they are validated. */
const HEX_COLOR = /^#?([\da-f]{3}|[\da-f]{6})$/i;

/**
 * Normalize an operator-entered colour to `#rrggbb`, or `null` if it is not a hex colour.
 * The value ends up in a CSS custom property, so anything else is discarded rather than injected.
 */
export function normalizeHexColor(value?: string | null): string | null {
  const hex = value?.trim();
  if (!hex || !HEX_COLOR.test(hex)) return null;
  const digits = hex.replace('#', '');
  return `#${digits.length === 3 ? [...digits].map((d) => d + d).join('') : digits}`.toLowerCase();
}

/**
 * Black or white — whichever stays readable on `hex`. Fair colours are arbitrary, so the text
 * placed on top of them cannot be hardcoded to white.
 *
 * WCAG relative luminance; the 0.179 threshold is the point where black and white contrast
 * equally against the background.
 */
export function contrastInk(hex: string): string {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return '#ffffff';
  const channel = (offset: number) => {
    const c = parseInt(normalized.substring(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance > 0.179 ? '#000000' : '#ffffff';
}
